import {Component, ElementRef, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {AiChatStateService} from "../../service/ai-chat-state.service";
import {HelpChatService} from "../../service/api/help-chat.service";
import {HelpContextService} from "../../service/api/help-context.service";
import {IAiChatMessage} from "../../interface/ai-chat.interface";
import {HelpAskRequestDto, HelpConversationDto} from "../../model/dto/help-chat.dto";
import {HelpSuggestedQuestionDto} from "../../model/dto/help-context.dto";

/**
 * Global AI Chat floating panel.
 * Renders as a fixed panel on the right side of the screen.
 * Communicates with CbHelpChatController via HelpChatService.
 */
@Component({
    selector: 'mvs-ai-chat-panel',
    templateUrl: './ai-chat-panel.component.html',
    styleUrls: ['./ai-chat-panel.component.scss'],
    standalone: false
})
export class AiChatPanelComponent implements OnInit, OnChanges, OnDestroy {

    @ViewChild('messageContainer') messageContainer: ElementRef;
    @ViewChild('questionInput') questionInput: ElementRef;

    initialized = false;
    isOpen = false;
    isLoading = false;
    currentModule: string | null = null;
    messages: IAiChatMessage[] = [];
    suggestedQuestions: HelpSuggestedQuestionDto[] = [];
    conversations: HelpConversationDto[] = [];
    showHistory = false;
    questionText = '';

    private subscriptions: Subscription[] = [];

    constructor(
        private chatStateService: AiChatStateService,
        private chatService: HelpChatService,
        private contextService: HelpContextService
    ) {}

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.subscriptions.push(
            this.chatStateService.panelOpen$.subscribe(open => {
                this.isOpen = open;
                if (open) {
                    this.loadSuggestedQuestions();
                    setTimeout(() => this.focusInput(), 100);
                }
            }),
            this.chatStateService.currentModule$.subscribe(module => {
                if (module !== this.currentModule) {
                    this.currentModule = module;
                    if (module) {
                        this.loadSuggestedQuestions();
                    }
                }
            }),
            this.chatStateService.messages$.subscribe(msgs => this.messages = msgs),
            this.chatStateService.loading$.subscribe(loading => this.isLoading = loading)
        );
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    onClose(): void {
        this.chatStateService.closePanel();
    }

    onNewConversation(): void {
        this.chatStateService.startNewConversation();
        this.showHistory = false;
        this.loadSuggestedQuestions();
    }

    onToggleHistory(): void {
        this.showHistory = !this.showHistory;
        if (this.showHistory) {
            this.loadConversations();
        }
    }

    onSendQuestion(): void {
        if (!this.questionText.trim() || this.isLoading || !this.currentModule) {
            return;
        }

        const question = this.questionText.trim();
        this.questionText = '';

        // Add user message to UI
        const userMessage: IAiChatMessage = {
            role: 'user',
            content: question,
            timestamp: new Date()
        };
        this.chatStateService.addMessage(userMessage);

        // Send to API
        this.chatStateService.setLoading(true);
        const request: HelpAskRequestDto = {
            question: question,
            moduleAlias: this.currentModule,
            conversationId: this.chatStateService.currentConversationId
        };

        this.chatService.ask(request).subscribe({
            next: response => {
                const assistantMessage: IAiChatMessage = {
                    role: 'assistant',
                    content: response.answer,
                    timestamp: new Date(),
                    sources: response.sources,
                    confidenceScore: response.confidenceScore
                };
                this.chatStateService.addMessage(assistantMessage);
                this.chatStateService.setConversationId(response.conversationId);
                this.chatStateService.setLoading(false);
                this.scrollToBottom();
            },
            error: err => {
                const errorMessage: IAiChatMessage = {
                    role: 'assistant',
                    content: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
                    timestamp: new Date()
                };
                this.chatStateService.addMessage(errorMessage);
                this.chatStateService.setLoading(false);
                console.error('AI Chat error:', err);
            }
        });

        this.scrollToBottom();
    }

    onSuggestedQuestionClick(question: HelpSuggestedQuestionDto): void {
        this.questionText = question.question;
        this.onSendQuestion();
    }

    onConversationClick(conversation: HelpConversationDto): void {
        this.chatStateService.setConversationId(conversation.id);
        this.showHistory = false;

        // Load messages for the conversation
        this.chatService.getConversationMessages(conversation.id).subscribe({
            next: messages => {
                const chatMessages: IAiChatMessage[] = messages.map(m => ({
                    role: m.role as any,
                    content: m.content,
                    timestamp: new Date(m.messageTimestamp),
                    sources: m.sourceReferencesJson ? JSON.parse(m.sourceReferencesJson) : undefined,
                    confidenceScore: m.confidenceScore
                }));
                this.chatStateService.setMessages(chatMessages);
                this.scrollToBottom();
            },
            error: err => console.error('Failed to load conversation messages', err)
        });
    }

    onDeleteConversation(conversation: HelpConversationDto, event: Event): void {
        event.stopPropagation();
        this.chatService.deleteConversation(conversation.id).subscribe({
            next: () => {
                this.conversations = this.conversations.filter(c => c.id !== conversation.id);
            },
            error: err => console.error('Failed to delete conversation', err)
        });
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSendQuestion();
        }
    }

    private loadSuggestedQuestions(): void {
        if (!this.currentModule) return;

        this.contextService.getSuggestedQuestions(this.currentModule).subscribe({
            next: questions => this.suggestedQuestions = questions,
            error: err => console.error('Failed to load suggested questions', err)
        });
    }

    private loadConversations(): void {
        this.chatService.getConversations(this.currentModule).subscribe({
            next: conversations => this.conversations = conversations,
            error: err => console.error('Failed to load conversations', err)
        });
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            if (this.messageContainer) {
                const el = this.messageContainer.nativeElement;
                el.scrollTop = el.scrollHeight;
            }
        }, 50);
    }

    private focusInput(): void {
        if (this.questionInput) {
            this.questionInput.nativeElement.focus();
        }
    }
}
